import React from "react";
import { Box } from "@mui/material";
import { resolveSrc } from "../../../api/endpoints";
import purifyHTML from "../../../utils/purify-html";

const EMBED_IMAGE_URL_REGEX = /\.(png|jpe?g|gif|webp|bmp|svg)(\?.*)?$/i;

const extractEmbeddedWidgetImageSrc = (rawHtml = "") => {
  const html = rawHtml.trim();
  if (!html) return null;

  const imageTagMatch = html.match(/^<img\b[^>]*\bsrc=["']([^"']+)["'][^>]*\/?>(?:\s*)$/i);
  if (imageTagMatch?.[1]) {
    return imageTagMatch[1].replaceAll("&amp;", "&");
  }

  const iframeTagMatch = html.match(/^<iframe\b[^>]*\bsrc=["']([^"']+)["'][^>]*><\/iframe>(?:\s*)$/i);
  const iframeSrc = iframeTagMatch?.[1]?.replaceAll("&amp;", "&");
  if (iframeSrc && EMBED_IMAGE_URL_REGEX.test(iframeSrc)) {
    return iframeSrc;
  }

  return null;
};

const normalizeEmbedHtml = (rawHtml = "") => {
  const html = rawHtml || "";
  return html.replace(/<iframe\b[^>]*>/gi, (iframeTag) => {
    let normalizedTag = iframeTag.replace(/\swidth=["'][^"']*["']/gi, "").replace(/\sheight=["'][^"']*["']/gi, "");

    if (/\sscrolling=/i.test(normalizedTag)) {
      normalizedTag = normalizedTag.replace(/\sscrolling=["'][^"']*["']/gi, ' scrolling="no"');
    } else {
      normalizedTag = normalizedTag.replace(/>$/, ' scrolling="no">');
    }

    const forcedFrameStyle =
      "width:100% !important;height:100% !important;max-width:100% !important;max-height:100% !important;overflow:hidden;";

    if (/\sstyle=["'][^"']*["']/i.test(normalizedTag)) {
      normalizedTag = normalizedTag.replace(/\sstyle=["']([^"']*)["']/i, (_match, styleValue) => ` style=\"${styleValue};${forcedFrameStyle}\"`);
    } else {
      normalizedTag = normalizedTag.replace(/>$/, ` style=\"${forcedFrameStyle}\">`);
    }

    return normalizedTag;
  });
};

const extractIframeSrc = (rawHtml = "") => {
  const html = rawHtml.trim();
  if (!html) return "";
  const iframeTagMatch = html.match(/^<iframe\b[^>]*\bsrc=["']([^"']+)["'][^>]*>(?:<\/iframe>)?(?:\s*)$/i);
  return iframeTagMatch?.[1]?.replaceAll("&amp;", "&") || "";
};

const isDirectUrl = (value = "") => /^https?:\/\//i.test(value.trim());

const CardEmbedPreview = ({ item }) => {
  const { inputValue } = item;
  const rawInput = inputValue || "";
  const embeddedWidgetImageSrc = extractEmbeddedWidgetImageSrc(rawInput);
  const iframeSrc = extractIframeSrc(rawInput);
  const directUrl = isDirectUrl(rawInput) ? rawInput.trim() : "";
  const previewWebsiteUrl = iframeSrc || directUrl;
  const previewImageUrl = embeddedWidgetImageSrc || (EMBED_IMAGE_URL_REGEX.test(directUrl) ? directUrl : "");

  const normalizedHtml = normalizeEmbedHtml(rawInput);
  const safeHtml = purifyHTML.sanitize(normalizedHtml, {
    ADD_TAGS: ["iframe", "embed", "object", "style"],
    ADD_ATTR: ["style", "class", "src", "width", "height", "scrolling", "frameborder", "allow", "allowfullscreen"],
  });

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        p: 2,
      }}>
      <Box
        sx={{
          position: "relative",
          width: "min(92vw, 980px)",
          height: "min(70vh, 560px)",
          backgroundColor: "#000",
          overflow: "hidden",
          borderRadius: 1,
          "& iframe, & embed, & object": {
            width: "100% !important",
            height: "100% !important",
            maxWidth: "100% !important",
            maxHeight: "100% !important",
            border: 0,
            display: "block",
          },
        }}>
        {previewImageUrl ? (
          <Box
            component="img"
            src={resolveSrc(previewImageUrl)}
            alt="Embed preview"
            sx={{
              width: "100%",
              height: "100%",
              objectFit: "contain",
              display: "block",
            }}
          />
        ) : previewWebsiteUrl ? (
          <>
            <Box
              component="iframe"
              src={previewWebsiteUrl}
              title="Embed website preview"
              scrolling="no"
              referrerPolicy="no-referrer-when-downgrade"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
              sx={{
                width: "100%",
                height: "100%",
                border: 0,
                display: "block",
                backgroundColor: "#fff",
              }}
            />
            <Box
              component="a"
              href={previewWebsiteUrl}
              target="_blank"
              rel="noreferrer"
              sx={{
                position: "absolute",
                right: 12,
                bottom: 10,
                px: 1,
                py: 0.5,
                fontSize: 12,
                borderRadius: 1,
                textDecoration: "none",
                color: "#fff",
                bgcolor: "rgba(0,0,0,0.55)",
              }}>
              Open in new tab
            </Box>
          </>
        ) : (
          <Box
            sx={{
              width: "100%",
              height: "100%",
              overflow: "hidden",
            }}
            dangerouslySetInnerHTML={{ __html: safeHtml }}
          />
        )}
      </Box>
    </Box>
  );
};

export default CardEmbedPreview;
